/**
 * HotlinkConfirmPage.tsx
 *
 * Standalone public page that offboarding task assignees reach via the
 * confirmation link in their daily reminder email.
 *
 * Uses the `term-` hotlink token from the URL query parameter as the sole
 * credential — no session JWT is required.
 *
 * Route: /terminations/hotlink/confirm (registered as a noLayout CustomRoute)
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { ImageProofDropzone } from './ImageProofDropzone';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

/** The one task type that uses the proof-gated pending -> processing -> done flow. */
const PROOF_GATED_TASK_TYPE = 'social_insurance_closing';

/**
 * Summary returned by `GET /terminations/hotlink/summary`.
 */
interface TerminationSummary {
    employee_full_name: string;
    task_label: string;
    due_date: string;
    task_type: string;
    status: 'pending' | 'processing' | 'done';
    has_processing_proof: boolean;
    has_done_proof: boolean;
}

type PageState = 'loading' | 'summary' | 'confirming' | 'success' | 'error';

/**
 * Public hotlink confirmation page for termination checklist tasks.
 *
 * Flow:
 * 1. On mount, reads the `?token=...` query parameter.
 * 2. Calls `GET /terminations/hotlink/summary` to fetch the task summary
 *    (without consuming the token).
 * 3. Displays the employee name, task label, and due date.
 * 4. For the three legacy task types, clicking **Confirm** calls
 *    `PUT /terminations/hotlink/confirm`, which consumes the token and flips
 *    the item to `done`.
 * 5. For `social_insurance_closing`, an {@link ImageProofDropzone} plus a
 *    step-appropriate button is shown instead — "Mark Processing" (pending)
 *    or "Mark Done" (processing) — which POSTs to
 *    `/terminations/hotlink/proof`. The token is only consumed once the
 *    `done` upload succeeds, so it survives the intermediate `processing` call.
 *
 * The token is passed as a `Bearer` Authorization header to avoid leaking it
 * in server request logs from a second query-parameter hop.
 */
export const HotlinkConfirmPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') ?? '';

    const [pageState, setPageState] = useState<PageState>('loading');
    const [summary, setSummary] = useState<TerminationSummary | null>(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [proofFile, setProofFile] = useState<File | null>(null);
    const [proofUploading, setProofUploading] = useState(false);

    // Fetch the task summary on mount
    useEffect(() => {
        if (!token) {
            setErrorMessage('No token provided in URL');
            setPageState('error');
            return;
        }

        const fetchSummary = async () => {
            try {
                const response = await fetch(`${API_URL}/terminations/hotlink/summary`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (response.status === 409) {
                    setErrorMessage('This confirmation link has already been used.');
                    setPageState('error');
                    return;
                }
                if (response.status === 410) {
                    setErrorMessage('This confirmation link has expired.');
                    setPageState('error');
                    return;
                }
                if (!response.ok) {
                    setErrorMessage('Unable to load task details. The link may be invalid.');
                    setPageState('error');
                    return;
                }
                const data = await response.json() as TerminationSummary;
                setSummary(data);
                setPageState('summary');
            } catch {
                setErrorMessage('Network error — please try again later.');
                setPageState('error');
            }
        };

        void fetchSummary();
    }, [token]);

    const handleConfirm = async () => {
        setPageState('confirming');
        try {
            const response = await fetch(`${API_URL}/terminations/hotlink/confirm`, {
                method: 'PUT',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) {
                const json = await response.json().catch(() => ({})) as { message?: string };
                setErrorMessage(json.message ?? 'Confirmation failed');
                setPageState('error');
                return;
            }
            setPageState('success');
        } catch {
            setErrorMessage('Network error — please try again later.');
            setPageState('error');
        }
    };

    const handleProofUpload = async (targetStatus: 'processing' | 'done') => {
        if (!proofFile) return;
        setProofUploading(true);
        try {
            const fd = new FormData();
            fd.append('target_status', targetStatus);
            fd.append('file', proofFile);
            const response = await fetch(`${API_URL}/terminations/hotlink/proof`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: fd,
            });
            if (!response.ok) {
                const json = await response.json().catch(() => ({})) as { message?: string };
                setErrorMessage(json.message ?? 'Upload failed');
                setPageState('error');
                return;
            }
            const updated = await response.json() as { status: TerminationSummary['status'] };
            if (updated.status === 'done') {
                setPageState('success');
                return;
            }
            setSummary((prev) => (prev ? { ...prev, status: updated.status } : prev));
            setProofFile(null);
        } catch {
            setErrorMessage('Network error — please try again later.');
            setPageState('error');
        } finally {
            setProofUploading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f5f5f5',
                p: 2,
            }}
        >
            <Card sx={{ maxWidth: 480, width: '100%' }}>
                <CardContent sx={{ p: 4 }}>
                    {/* Loading */}
                    {(pageState === 'loading' || pageState === 'confirming') && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {/* Summary — awaiting user confirmation */}
                    {pageState === 'summary' && summary && summary.task_type !== PROOF_GATED_TASK_TYPE && (
                        <>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                Offboarding Task Confirmation
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Please confirm that the following offboarding task has been completed:
                            </Typography>
                            <Box sx={{ mb: 3, p: 2, background: '#fafafa', borderRadius: 1, border: '1px solid #eee' }}>
                                <Typography variant="body2"><strong>Employee:</strong> {summary.employee_full_name}</Typography>
                                <Typography variant="body2"><strong>Task:</strong> {summary.task_label}</Typography>
                                <Typography variant="body2"><strong>Due Date:</strong> {summary.due_date}</Typography>
                            </Box>
                            <Button
                                variant="contained"
                                color="success"
                                fullWidth
                                startIcon={<CheckCircleOutlineIcon />}
                                onClick={handleConfirm}
                                data-testid="hotlink-confirm-button"
                            >
                                Confirm Task Completed
                            </Button>
                        </>
                    )}

                    {/* Summary — proof-gated social_insurance_closing flow */}
                    {pageState === 'summary' && summary && summary.task_type === PROOF_GATED_TASK_TYPE && (
                        <>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                Offboarding Task Confirmation
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                {summary.status === 'processing'
                                    ? 'Please upload proof that this task has been completed:'
                                    : 'Please upload proof that this task is now being processed:'}
                            </Typography>
                            <Box sx={{ mb: 3, p: 2, background: '#fafafa', borderRadius: 1, border: '1px solid #eee' }}>
                                <Typography variant="body2"><strong>Employee:</strong> {summary.employee_full_name}</Typography>
                                <Typography variant="body2"><strong>Task:</strong> {summary.task_label}</Typography>
                                <Typography variant="body2"><strong>Due Date:</strong> {summary.due_date}</Typography>
                            </Box>
                            <ImageProofDropzone
                                label={summary.status === 'processing' ? 'Done proof' : 'Processing proof'}
                                onFileReady={setProofFile}
                                disabled={proofUploading}
                            />
                            <Button
                                variant="contained"
                                color="success"
                                fullWidth
                                sx={{ mt: 2 }}
                                startIcon={<CheckCircleOutlineIcon />}
                                onClick={() => handleProofUpload(summary.status === 'processing' ? 'done' : 'processing')}
                                disabled={!proofFile || proofUploading}
                                data-testid="hotlink-proof-upload-button"
                            >
                                {proofUploading
                                    ? 'Uploading…'
                                    : summary.status === 'processing' ? 'Mark Done' : 'Mark Processing'}
                            </Button>
                        </>
                    )}

                    {/* Success */}
                    {pageState === 'success' && (
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                            <Typography variant="h6" sx={{ mb: 1 }}>Task Confirmed</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Thank you — this offboarding task has been marked as completed.
                            </Typography>
                        </Box>
                    )}

                    {/* Error */}
                    {pageState === 'error' && (
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                            <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                            <Typography variant="h6" sx={{ mb: 1 }}>Unable to Confirm</Typography>
                            <Alert severity="error" sx={{ mt: 1 }}>
                                {errorMessage}
                            </Alert>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};
